import { getParentCategoryFromSubCategory } from '@eventbrite/categories';
import { Checkbox } from '@eventbrite/marmalade';
import loadable from '@loadable/component';
import queryString from 'query-string';
import React, { useState } from 'react';
import {
    CUSTOM_DATE,
    DATES_MAPPING_SEARCH,
    EVENTBRITE_CATEGORY_MAPPING_SEARCH,
    EVENTBRITE_FORMAT_MAPPING_SEARCH,
    PRICE_MAPPING,
} from '../../../../constants';
import {
    CATEGORY_FILTER,
    CURRENCY_FILTER,
    DATE_FILTER,
    FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP,
    FORMATTED_CURRENCIES,
    FORMATTED_LANGUAGES,
    FORMAT_FILTER,
    LANGUAGE_FILTER,
    PRICE_FILTER,
    SUBCATEGORIES_FILTER,
} from '../../../../constants/filters';
import { CATEGORY_SUBCATEGORY_MAPPING_SEARCH } from '../../constants';
import { FilterItem, FilterSection } from './FilterSection';
import FollowedOrganizersFilterButton from './FollowedOrganizersFilterButton';
import { NestedFilterItem } from './NestedFilterItem';
import OnlineEventsFilterButton from './OnlineEventsFilterButton';
import { FilterProps, RadioType } from './types';

const InlineDatePicker = loadable(() => import('./InlineDatePicker'));

const constructFilterItemKey = (
    filter: string,
    filterItemValue: string,
    isMobile: boolean,
): string => {
    return `${filter}-${filterItemValue}-${isMobile ? 'mobile' : 'desktop'}`;
};

const DateFilters: React.FunctionComponent<FilterProps> = (props) => {
    const urlParams = queryString.parse(props.location.search.replace('?', ''));
    const [currentDateOption, setCurrentDateOption] = useState<string>('');
    const activeFilterOption = props.selectedFilters?.[DATE_FILTER]?.toString();

    const handleOptionChange = (value: string) => {
        if (value !== CUSTOM_DATE) {
            props.onFilterChange?.(DATE_FILTER, value);
            setCurrentDateOption(value);
        } else if (currentDateOption === CUSTOM_DATE) {
            setCurrentDateOption('');
        } else {
            setCurrentDateOption(value);
        }
    };

    const handleCustomDateSelect = (startDate: string, endDate: string) => {
        setCurrentDateOption('');

        props?.onCustomDateChange?.(startDate, endDate);
    };

    return (
        <FilterSection
            filters={{
                ...DATES_MAPPING_SEARCH,
            }}
            onOptionChange={(val: string) => handleOptionChange(val)}
            title={FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[DATE_FILTER].label}
            activeFilterOption={activeFilterOption}
            isMobile={props.isMobile}
            shouldScrollIntoView={props.filterTypeToShow === DATE_FILTER}
        >
            {(filterItem: RadioType) => {
                const shouldRenderDatePicker =
                    currentDateOption === CUSTOM_DATE &&
                    filterItem.value === CUSTOM_DATE;
                return (
                    <FilterItem
                        key={constructFilterItemKey(
                            'date',
                            filterItem.value,
                            props.isMobile,
                        )}
                        value={filterItem.value}
                        display={filterItem.display}
                    >
                        {shouldRenderDatePicker ? (
                            <InlineDatePicker
                                onDatePickerChange={handleCustomDateSelect}
                                urlParams={urlParams}
                            />
                        ) : null}
                    </FilterItem>
                );
            }}
        </FilterSection>
    );
};

const PriceFilters: React.FunctionComponent<FilterProps> = (props) => {
    return (
        <FilterSection
            filters={{
                ...PRICE_MAPPING,
            }}
            onOptionChange={(value: any) => {
                props.onFilterChange?.(PRICE_FILTER, value);
            }}
            title={FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[PRICE_FILTER].label}
            activeFilterOption={props.selectedFilters?.[PRICE_FILTER]}
            isMobile={props.isMobile}
            shouldScrollIntoView={props.filterTypeToShow === PRICE_FILTER}
        >
            {(filterItem: RadioType) => {
                return (
                    <FilterItem
                        key={constructFilterItemKey(
                            'price',
                            filterItem.value,
                            props.isMobile,
                        )}
                        value={filterItem.value}
                        display={filterItem.display}
                    />
                );
            }}
        </FilterSection>
    );
};

const CategoryFilters: React.FunctionComponent<
    FilterProps & { subcategoryFiltersExperimentEnabled?: boolean }
> = (props) => {
    const Component = props.subcategoryFiltersExperimentEnabled
        ? NestedCategoryFilters
        : OnlyCategoryFilters;
    return <Component {...props} />;
};

const OnlyCategoryFilters: React.FunctionComponent<FilterProps> = (props) => {
    return (
        <FilterSection
            filters={{ ...EVENTBRITE_CATEGORY_MAPPING_SEARCH }}
            onOptionChange={(value: any) => {
                props.onFilterChange?.(CATEGORY_FILTER, value);
            }}
            title={
                FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[CATEGORY_FILTER].label
            }
            activeFilterOption={props.selectedFilters?.[CATEGORY_FILTER]}
            isMobile={props.isMobile}
            shouldScrollIntoView={props.filterTypeToShow === CATEGORY_FILTER}
        >
            {(filterItem: RadioType) => {
                return (
                    <FilterItem
                        key={constructFilterItemKey(
                            'category',
                            filterItem.value,
                            props.isMobile,
                        )}
                        value={filterItem.value}
                        display={filterItem.display}
                    />
                );
            }}
        </FilterSection>
    );
};

const NestedCategoryFilters: React.FunctionComponent<FilterProps> = (props) => {
    const category = props.selectedFilters?.[CATEGORY_FILTER];
    const subcategories = props.selectedFilters?.[SUBCATEGORIES_FILTER];
    const parentCategories = subcategories?.map((subcategory) =>
        getParentCategoryFromSubCategory(subcategory),
    );

    return (
        <FilterSection
            filters={EVENTBRITE_CATEGORY_MAPPING_SEARCH}
            onOptionChange={(value: any) => {
                props.onFilterChange?.(CATEGORY_FILTER, value);
            }}
            title={
                FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[CATEGORY_FILTER].label
            }
            activeFilterOption={category}
            isMobile={props.isMobile}
            shouldScrollIntoView={props.filterTypeToShow === CATEGORY_FILTER}
        >
            {(filterItem: RadioType) => {
                return (
                    <NestedFilterItem
                        key={constructFilterItemKey(
                            'category',
                            filterItem.value,
                            props.isMobile,
                        )}
                        value={filterItem.value}
                        display={filterItem.display}
                        activeFilterOption={subcategories}
                        subFilterMap={CATEGORY_SUBCATEGORY_MAPPING_SEARCH}
                        isMobile={false}
                        onOptionChange={(value: any) => {
                            props.onFilterChange?.(SUBCATEGORIES_FILTER, value);
                        }}
                        tokens={{
                            '--NestedFilterItemMargin': '0 0 0 16px',
                            '--FilterSectionMargin': '24px 0 24px 0',
                            '--FieldSetLegendPadding': '0 0 0.75rem 0',
                            '--LiPadding': '16px 0 0 0',
                        }}
                        initialToggleOpen={parentCategories?.includes(
                            filterItem.value,
                        )}
                    />
                );
            }}
        </FilterSection>
    );
};

const FormatFilters: React.FunctionComponent<FilterProps> = (props) => {
    return (
        <FilterSection
            filters={{
                ...EVENTBRITE_FORMAT_MAPPING_SEARCH,
            }}
            onOptionChange={(value: any) => {
                props.onFilterChange?.(FORMAT_FILTER, value);
            }}
            title={FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[FORMAT_FILTER].label}
            activeFilterOption={props.selectedFilters?.[FORMAT_FILTER]}
            shouldScrollIntoView={props.filterTypeToShow === FORMAT_FILTER}
            isMobile={props.isMobile}
        >
            {(filterItem: RadioType) => {
                return (
                    <FilterItem
                        key={constructFilterItemKey(
                            'format',
                            filterItem.value,
                            props.isMobile,
                        )}
                        value={filterItem.value}
                        display={filterItem.display}
                    />
                );
            }}
        </FilterSection>
    );
};

const LanguageFilters: React.FunctionComponent<FilterProps> = (props) => {
    return (
        <FilterSection
            filters={{
                ...FORMATTED_LANGUAGES,
            }}
            onOptionChange={(value: any) => {
                props.onFilterChange?.(LANGUAGE_FILTER, value);
            }}
            title={
                FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[LANGUAGE_FILTER].label
            }
            activeFilterOption={props.selectedFilters?.[LANGUAGE_FILTER]}
            shouldScrollIntoView={props.filterTypeToShow === LANGUAGE_FILTER}
            defaultActiveFilterOption={[]}
            isMobile={props.isMobile}
        >
            {(filterItem: RadioType) => {
                return (
                    <FilterItem
                        ChoiceComponent={Checkbox}
                        key={constructFilterItemKey(
                            'language',
                            filterItem.value,
                            props.isMobile,
                        )}
                        value={filterItem.value}
                        display={filterItem.display}
                    />
                );
            }}
        </FilterSection>
    );
};

const CurrencyFilters: React.FunctionComponent<FilterProps> = (props) => {
    return (
        <FilterSection
            filters={{
                ...FORMATTED_CURRENCIES,
            }}
            onOptionChange={(value: any) => {
                props.onFilterChange?.(CURRENCY_FILTER, value);
            }}
            activeFilterOption={props.selectedFilters?.[CURRENCY_FILTER]}
            shouldScrollIntoView={props.filterTypeToShow === CURRENCY_FILTER}
            title={
                FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[CURRENCY_FILTER].label
            }
            isMobile={props.isMobile}
        >
            {(filterItem: RadioType) => {
                return (
                    <FilterItem
                        key={constructFilterItemKey(
                            'currency',
                            filterItem.value,
                            props.isMobile,
                        )}
                        value={filterItem.value}
                        display={filterItem.display}
                    />
                );
            }}
        </FilterSection>
    );
};

const Filters: React.FunctionComponent<FilterProps> = (props) => {
    return (
        <div>
            <FollowedOrganizersFilterButton {...props} />
            <OnlineEventsFilterButton {...props} />
            <DateFilters {...props} />
            <PriceFilters {...props} />
            <CategoryFilters {...props} />
            <FormatFilters {...props} />
            <LanguageFilters {...props} />
            <CurrencyFilters {...props} />
        </div>
    );
};

export default Filters;
